import React, { useEffect, useState } from "react";
import { Loader2, Save, LayoutTemplate, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "@/admin/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { CONTENT_DEFAULTS } from "@/lib/content";

const Section = ({ title, desc, children }) => (
  <div className="rounded-wp-lg border border-border bg-card p-5 shadow-wp-soft">
    <h2 className="font-display text-lg">{title}</h2>
    {desc && <p className="mb-4 mt-0.5 text-sm text-wp-ink-muted">{desc}</p>}
    <div className={desc ? "space-y-3" : "mt-4 space-y-3"}>{children}</div>
  </div>
);

// Section schema. `root:true` fields reuse existing top-level settings keys
// (single source of truth). Others live under content.* (new, no duplication).
const SECTIONS = [
  {
    title: "Homepage",
    desc: "Announcement, hero and special-gifting copy. (Hero & announcement share the same values as Homepage & Settings.)",
    fields: [
      { k: "announcement", label: "Announcement bar text", type: "text", root: true },
      { k: "hero.headline", label: "Hero headline", type: "text", root: true },
      { k: "hero.subheading", label: "Hero supporting copy", type: "textarea", root: true },
      { k: "special_gifting_title", label: "Special gifting — heading", type: "text" },
      { k: "special_gifting_subtitle", label: "Special gifting — subheading", type: "textarea" },
      { k: "corporate_card_title", label: "Corporate card — title", type: "text" },
      { k: "corporate_card_copy", label: "Corporate card — copy", type: "textarea" },
      { k: "silver_card_title", label: "Custom Silver card — title", type: "text" },
      { k: "silver_card_copy", label: "Custom Silver card — copy", type: "textarea" },
    ],
  },
  {
    title: "Checkout & Payments",
    fields: [
      { k: "checkout_subtitle", label: "Checkout supporting copy", type: "textarea" },
      { k: "payment_value_note", label: "Payment value message", type: "textarea" },
    ],
  },
  {
    title: "Packaging & Shipping",
    desc: "Tip: type {shipping_charge} and it is replaced automatically with the live shipping amount from Commerce settings.",
    fields: [
      { k: "shipping_note", label: "Shipping / remote-location message", type: "textarea" },
      { k: "standard_packaging_desc", label: "Standard packaging description", type: "textarea" },
      { k: "signature_packaging_desc", label: "Signature packaging description", type: "textarea" },
    ],
  },
  {
    title: "Build Your Parcel",
    fields: [
      { k: "builder_title", label: "Page heading", type: "text" },
      { k: "builder_subtitle", label: "Supporting text", type: "textarea" },
      { k: "builder_spend_guide_note", label: "Spend guide explanatory copy", type: "textarea" },
    ],
  },
  {
    title: "Personalisation",
    fields: [
      { k: "personalize_heading", label: "Make It Personal — heading", type: "text" },
      { k: "personalize_subcopy", label: "Make It Personal — supporting copy", type: "textarea" },
      { k: "personalize_surprise_note", label: "Surprise / QR explanation", type: "textarea" },
    ],
  },
  {
    title: "Footer / General Brand Copy",
    fields: [
      { k: "footer_brand_copy", label: "Footer brand description", type: "textarea" },
    ],
  },
];

export default function ContentManager() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.get("/settings").then((r) => setS(r.data || {})).catch(() => setS({})); }, []);

  const setPath = (path, value) => {
    setS((prev) => {
      const keys = path.split(".");
      const clone = JSON.parse(JSON.stringify(prev || {}));
      let cur = clone;
      for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = cur[keys[i]] || {}; cur = cur[keys[i]]; }
      cur[keys[keys.length - 1]] = value;
      return clone;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { key, seed_version, _id, ...payload } = s || {};
      const { data } = await adminApi.put("/settings", payload);
      setS(data);
      toast.success("Content saved \uD83D\uDC9D");
    } catch { toast.error("Could not save content"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-wp-lg" />)}</div>;

  const content = s.content || {};
  const valueOf = (f) => {
    if (f.root) {
      const keys = f.k.split(".");
      let cur = s;
      for (const kk of keys) { cur = cur?.[kk]; }
      return cur ?? "";
    }
    return content[f.k] ?? "";
  };
  const onChangeField = (f, v) => setPath(f.root ? f.k : `content.${f.k}`, v);
  const placeholderOf = (f) => (f.root ? "" : (CONTENT_DEFAULTS[f.k] || ""));

  // Personalisation Explainer (optional, independently toggleable)
  const ex = content.personalization_explainer || {};
  const exEnabled = ex.enabled ?? CONTENT_DEFAULTS.personalization_explainer.enabled;
  const exSteps = (Array.isArray(ex.steps) && ex.steps.length ? ex.steps : CONTENT_DEFAULTS.personalization_explainer.steps);
  const updateStep = (i, patch) => {
    const next = exSteps.map((st, idx) => (idx === i ? { ...st, ...patch } : { ...st }));
    setPath("content.personalization_explainer.steps", next);
  };

  return (
    <div data-testid="admin-content-manager">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">Content Manager</h1>
          <p className="text-sm text-wp-ink-muted">Edit everyday website wording without code. Technical/system messages stay in code for safety.</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-xl bg-wp-rose-strong text-white hover:brightness-105" data-testid="content-save-button">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save changes
        </Button>
      </div>

      <div className="space-y-5">
        {SECTIONS.map((sec) => (
          <Section key={sec.title} title={sec.title} desc={sec.desc}>
            {sec.fields.map((f) => (
              <div key={f.k}>
                <Label className="mb-1.5 block text-sm">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea rows={2} value={valueOf(f)} placeholder={placeholderOf(f)} onChange={(e) => onChangeField(f, e.target.value)} className="rounded-wp-md" data-testid={`content-${f.k.replace(/\./g, "-")}`} />
                ) : (
                  <Input value={valueOf(f)} placeholder={placeholderOf(f)} onChange={(e) => onChangeField(f, e.target.value)} className="rounded-wp-md" data-testid={`content-${f.k.replace(/\./g, "-")}`} />
                )}
              </div>
            ))}
          </Section>
        ))}

        {/* Personalisation Explainer — optional visual with its own ON/OFF */}
        <Section title="Personalisation Explainer" desc="An optional 3-step visual on the Make It Personal page. Turning it OFF hides it completely (no blank space) while keeping this content saved for later.">
          <div className="flex items-center justify-between rounded-wp-md border border-border bg-wp-surface px-4 py-3">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4 text-wp-rose-strong" />
              <span className="text-sm font-medium">Show Personalisation Explainer</span>
            </div>
            <Switch checked={exEnabled} onCheckedChange={(v) => setPath("content.personalization_explainer.enabled", v)} data-testid="content-explainer-toggle" />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">Explainer heading</Label>
            <Input value={ex.heading ?? ""} placeholder={CONTENT_DEFAULTS.personalization_explainer.heading} onChange={(e) => setPath("content.personalization_explainer.heading", e.target.value)} className="rounded-wp-md" data-testid="content-explainer-heading" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-wp-md border border-border p-3">
                <p className="mb-2 text-xs font-semibold text-wp-rose-strong">Step {i + 1}</p>
                <Label className="mb-1 block text-xs">Title</Label>
                <Input value={exSteps[i]?.title ?? ""} placeholder={CONTENT_DEFAULTS.personalization_explainer.steps[i].title} onChange={(e) => updateStep(i, { title: e.target.value })} className="mb-2 rounded-wp-md" data-testid={`content-explainer-step-${i + 1}-title`} />
                <Label className="mb-1 block text-xs">Copy</Label>
                <Textarea rows={2} value={exSteps[i]?.copy ?? ""} placeholder={CONTENT_DEFAULTS.personalization_explainer.steps[i].copy} onChange={(e) => updateStep(i, { copy: e.target.value })} className="rounded-wp-md" data-testid={`content-explainer-step-${i + 1}-copy`} />
              </div>
            ))}
          </div>
          <p className="text-xs text-wp-ink-muted">This ON/OFF pattern is reusable — future optional visuals (e.g. a homepage brand video) can each get their own independent toggle without affecting this one.</p>
        </Section>

        {/* Corporate & Custom Silver rich content live in their dedicated editors — reused, not duplicated */}
        <Section title="Corporate Gifting & Custom Silver" desc="Rich content (images, categories, samples, steps) for these pages is managed in their dedicated editors to keep a single source of truth.">
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/homepage" className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-wp-surface px-3 py-2 text-sm hover:border-wp-rose-strong/40" data-testid="content-link-homepage-settings">
              <ExternalLink className="h-3.5 w-3.5" /> Open Homepage &amp; Settings
            </Link>
          </div>
          <p className="text-xs text-wp-ink-muted">The card titles/copy shown on the homepage for Corporate &amp; Custom Silver are editable above under “Homepage”.</p>
        </Section>
      </div>
    </div>
  );
}
